import { Alert, Button, Form, Input, Modal, Popconfirm, Switch, Select } from "antd"
import { useState } from "react"
import type { PhysicalIdentity, PhysicalIdentityWrite, PhysicalKind } from "../../types/physical"
import { identityWrite, physicalApi, physicalError } from "../../utils/physicalApi"
import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import type { ICloudCustomer } from "../../types"
import { usePhysicalResource } from "./usePhysicalResource"
import { parseApiError } from "../../utils/regulatoryApi"
const names = {
  site: "site",
  asset: "équipement physique",
  component: "composant physique",
}
export function IdentityEditor({
  kind,
  value,
  parent,
  onSaved,
  onCancel,
}: {
  kind: PhysicalKind
  value?: PhysicalIdentity
  parent?: string
  onSaved: (value: PhysicalIdentity) => void
  onCancel: () => void
}) {
  const { currentEnterprise } = useSyncronizationContext()
  const tenant = value?.enterpriseUuid ?? currentEnterprise?.UUID
  const customers = usePhysicalResource(
    kind === "site" && tenant ? `physical-customers/${tenant}` : "",
    async (signal) => {
      const response = await fetch(
        `/api/enterprise/customers?enterprise_uuid=${encodeURIComponent(tenant!)}`,
        { signal, credentials: "same-origin", cache: "no-store" },
      )
      if (!response.ok) throw parseApiError(await response.text(), response.status)
      return (await response.json()) as ICloudCustomer[]
    },
  )
  const [form] = Form.useForm<PhysicalIdentityWrite>()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()
  const [conflict, setConflict] = useState(false)
  const [base, setBase] = useState(value)
  const initial = value ? identityWrite(value) : { name: "", code: "", notes: "", archived: false }
  const save = async () => {
    const fields = await form.validateFields().catch(() => null)
    if (!fields) return
    setBusy(true)
    setError(undefined)
    try {
      const body: PhysicalIdentityWrite = {
        ...fields,
        archived: fields.archived ?? false,
        ...(base ? { expectedVersion: base.version } : {}),
        ...(kind === "site"
          ? {
              customerUuid: fields.customerUuid ?? null,
              ...(!base && tenant ? { enterpriseUuid: tenant } : {}),
            }
          : kind === "asset"
            ? { physicalSiteUuid: parent ?? base?.physicalSiteUuid }
            : { physicalAssetUuid: parent ?? base?.physicalAssetUuid }),
      }
      onSaved(await physicalApi.save(kind, body, base?.uuid))
    } catch (e) {
      setError(physicalError(e))
      setConflict((e as { status?: number }).status === 409)
    } finally {
      setBusy(false)
    }
  }
  const reload = async () => {
    if (!base) return
    setBusy(true)
    try {
      const latest = await physicalApi.get(kind, base.uuid)
      setBase(latest)
      form.setFieldsValue(identityWrite(latest))
      setError(undefined)
      setConflict(false)
    } catch (e) {
      setError(physicalError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open
      title={`${value ? "Modifier le" : "Créer un"} ${names[kind]}`}
      onCancel={onCancel}
      onOk={save}
      confirmLoading={busy}
      cancelButtonProps={{ disabled: busy }}
      closable={!busy}
      maskClosable={false}
      okText="Enregistrer"
      cancelText="Annuler"
    >
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            conflict && base ? (
              <Popconfirm
                title="Remplacer votre saisie par la version du serveur ?"
                onConfirm={reload}
              >
                <Button disabled={busy}>Recharger le formulaire</Button>
              </Popconfirm>
            ) : undefined
          }
          style={{ marginBottom: 12 }}
        />
      )}
      <Form form={form} initialValues={initial} layout="vertical" disabled={busy}>
        <Form.Item
          name="name"
          label="Nom"
          rules={[{ required: true, whitespace: true, message: "Saisissez un nom" }, { max: 250 }]}
        >
          <Input maxLength={250} autoFocus />
        </Form.Item>
        <Form.Item name="code" label="Code interne">
          <Input maxLength={100} />
        </Form.Item>
        {kind === "site" && (
          <Form.Item name="address" label="Adresse">
            <Input.TextArea maxLength={2000} rows={2} />
          </Form.Item>
        )}
        {kind === "site" && (
          <Form.Item name="customerUuid" label="Client du site">
            <Select
              aria-label="Client du site"
              allowClear
              showSearch
              optionFilterProp="label"
              loading={customers.loading}
              options={[
                ...(base?.customerUuid && !customers.data?.some((c) => c.UUID === base.customerUuid)
                  ? [{ value: base.customerUuid, label: "Client actuel" }]
                  : []),
                ...(customers.data ?? []).map((c) => ({ value: c.UUID, label: c.Name })),
              ]}
            />
          </Form.Item>
        )}
        {customers.error && (
          <Alert
            type="warning"
            message="La liste des clients est indisponible. Le client actuel reste conservé."
          />
        )}
        {kind === "asset" && (
          <>
            <Form.Item name="manufacturer" label="Fabricant">
              <Input maxLength={250} />
            </Form.Item>
            <Form.Item name="model" label="Modèle">
              <Input maxLength={250} />
            </Form.Item>
            <Form.Item name="serialNumber" label="Numéro de série">
              <Input maxLength={250} />
            </Form.Item>
          </>
        )}
        <Form.Item name="notes" label="Notes de suivi du dossier">
          <Input.TextArea maxLength={6000} rows={4} showCount />
        </Form.Item>
        {value && (
          <Form.Item name="archived" label="Archivé" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
